import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

/**
 * Per-route filter persistence for tables that drive their state
 * via TanStack `useSearch` (q / status / assignee / …). Mirrors
 * the page-size pattern in `Pagination` (`storageKey` prop) but
 * for the filter set as a whole.
 *
 * Behaviour:
 *   * On mount (and whenever the route's pathname changes), if
 *     the URL carries NO filter values for any of the listed
 *     fields, restore them from `localStorage["hub:filters:<pathname>"]`
 *     via `navigate({ replace: true })`. The route's loader then
 *     re-runs with the restored values, no extra fetch round-trip.
 *   * On every user-driven URL change (after the first restore),
 *     write the current filter snapshot back to localStorage so
 *     a refresh / cross-page-back keeps the picks intact.
 *
 * The pathname is part of the key, so `/work-items` and
 * `/projects/<slug>/work-items` each remember their own set —
 * the global list's filters don't bleed into a project's view.
 */
const STORAGE_PREFIX = "hub:filters:";

type FilterValues = Record<string, string | number | boolean | undefined>;

function readStorage(key: string): FilterValues | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FilterValues;
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: FilterValues): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage disabled / full — silent no-op.
  }
}

export function usePersistedFilters(fields: readonly string[]): void {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const restoredForKey = useRef<string | null>(null);

  const storageKey = `${STORAGE_PREFIX}${pathname}`;

  // Restore — runs once per `storageKey`. When the route is
  // navigated to a different pathname, the key changes and the
  // restore fires again for that new key.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately keyed on storageKey so the restore fires per route, not per filter tick
  useEffect(() => {
    if (restoredForKey.current === storageKey) return;
    restoredForKey.current = storageKey;

    const urlHasFilters = fields.some((f) => {
      const v = search[f];
      return v !== undefined && v !== "";
    });
    if (urlHasFilters) return;

    const stored = readStorage(storageKey);
    if (!stored) return;

    const restoreFields: Record<string, unknown> = {};
    let any = false;
    for (const f of fields) {
      if (stored[f] !== undefined && stored[f] !== "") {
        restoreFields[f] = stored[f];
        any = true;
      }
    }
    if (!any) return;

    navigate({
      // @ts-expect-error — generic component; TanStack's typed
      // route helpers can't statically validate a runtime field list.
      search: (prev: Record<string, unknown>) => ({ ...prev, ...restoreFields }),
      replace: true,
    });
  }, [storageKey]);

  // Save — runs whenever the URL filters change AFTER restore has
  // fired for this key. Skipping the initial render avoids racing
  // with the restore (writes would happen before the restored
  // values made it into `search`).
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately keyed on filter values only
  useEffect(() => {
    if (restoredForKey.current !== storageKey) return;
    const snap: FilterValues = {};
    for (const f of fields) {
      const v = search[f];
      if (
        v !== undefined &&
        v !== "" &&
        (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
      ) {
        snap[f] = v;
      }
    }
    writeStorage(storageKey, snap);
  }, [storageKey, ...fields.map((f) => search[f])]);
}
